'use client';

import Image from 'next/image';
import Link from 'next/link';
import React from 'react';
import { FaCalendarAlt, FaClock, FaFacebookF, FaLinkedinIn, FaTwitter, FaUser, FaWhatsapp } from 'react-icons/fa';

import Breadcrumb from '@/components/Breadcrumb';
import Header from '@/components/Header';

import { useLanguage } from '@/contexts/LanguageContext';

export default function SiviYuzGermeNedirHangiIcerikliUrunlerKullanilirPage() {
  const { t } = useLanguage();
  const pageUrl = typeof window !== 'undefined' ? window.location.href : 'https://drbariscin.com/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir';
  const pageTitle = 'Sıvı Yüz Germe Nedir, Hangi İçerikli Ürünler Kullanılır? - Ameliyatsız Lifting';
  const pageDescription = 'Sıvı yüz germe işlemi nedir, nasıl yapılır, hangi içerikler kullanılır? Hyaluronik asit, kolajen ve vitamin karışımlarıyla ameliyatsız yüz gençleştirmenin modern yöntemi.';

  const handleShare = (platform: string) => {
    let shareUrl = '';
    
    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}`;
        break;
      case 'twitter':
        shareUrl = `https://twitter.com/intent/tweet?url=${encodeURIComponent(pageUrl)}&text=${encodeURIComponent(pageTitle)}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(pageUrl)}`;
        break;
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(pageTitle + ' - ' + pageUrl)}`;
        break;
      default:
        return;
    }
    
    window.open(shareUrl, '_blank', 'width=600,height=400');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-blue-100">
      <Header />
      
      <div className="max-w-4xl mx-auto px-4 sm:px-6 py-8">
        {/* Breadcrumb */}
        <div className="mb-8">
          <Breadcrumb
            items={[
              { label: 'Ana Sayfa', href: '/tr' },
              { label: 'Blog', href: '/tr/blog' },
              { label: 'Sıvı Yüz Germe Nedir?', href: '/tr/blog/sivi-yuz-germe-nedir-hangi-icerikli-urunler-kullanilir' },
            ]}
          />
        </div>

        {/* Article Header */}
        <article className="bg-white rounded-3xl shadow-xl overflow-hidden">
          {/* Featured Image */}
          <div className="relative h-64 sm:h-80">
            <Image
              src="/images/terleme-tedavisi.webp"
              alt="Sıvı yüz germe işlemi ve içerikler değerlendirmesi"
              fill
              className="object-cover"
              priority
            />
            <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
            <div className="absolute bottom-6 left-6">
              <span className="bg-indigo-600 text-white px-4 py-2 rounded-full text-sm font-medium">
                Sıvı Yüz Germe
              </span>
            </div>
          </div>

          {/* Article Content */}
          <div className="p-6 sm:p-8">
            {/* Title */}
            <h1 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-6">
              Sıvı Yüz Germe Nedir, Hangi İçerikli Ürünler Kullanılır?
            </h1>

            {/* Meta Information */}
            <div className="flex flex-wrap items-center gap-6 mb-8 text-sm text-gray-600 border-b border-gray-200 pb-6">
              <div className="flex items-center">
                <FaUser className="w-4 h-4 mr-2" />
                <span>Op. Dr. Barış Çin</span>
              </div>
              <div className="flex items-center">
                <FaCalendarAlt className="w-4 h-4 mr-2" />
                <span>25 Ocak 2026</span>
              </div>
              <div className="flex items-center">
                <FaClock className="w-4 h-4 mr-2" />
                <span>8 dakika okuma</span>
              </div>
            </div>

            {/* Share Buttons */}
            <div className="flex items-center gap-4 mb-8">
              <span className="text-gray-600 font-medium">Paylaş:</span>
              <div className="flex gap-3">
                <button className="p-2 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors" onClick={() => handleShare('facebook')}>
                  <FaFacebookF className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-400 text-white rounded-full hover:bg-blue-500 transition-colors" onClick={() => handleShare('twitter')}>
                  <FaTwitter className="w-4 h-4" />
                </button>
                <button className="p-2 bg-blue-700 text-white rounded-full hover:bg-blue-800 transition-colors" onClick={() => handleShare('linkedin')}>
                  <FaLinkedinIn className="w-4 h-4" />
                </button>
                <button className="p-2 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors" onClick={() => handleShare('whatsapp')}>
                  <FaWhatsapp className="w-4 h-4" />
                </button>
              </div>
            </div>

            {/* Article Content */}
            <div className="prose prose-lg max-w-none">
              <p className="text-lg text-gray-700 mb-6 leading-relaxed">
                Sıvı yüz germe, son yıllarda en çok tercih edilen ameliyatsız gençleştirme yöntemlerinden biridir. Cerrahi müdahale gerektirmeden, cilt altına özel içerikli maddelerin enjekte edilmesiyle doğal lifting etkisi yaratır. Yöntemin amacı, yüzün hacmini yeniden kazandırmak, cilt elastikiyetini artırmak ve yaşlanma belirtilerini yumuşatmaktır.
              </p>

              <p className="text-gray-700 mb-6 leading-relaxed font-medium">
                Kısacası, sıvı yüz germe; dolgu, botoks, mezoterapi ve kolajen desteklerinin kombine edildiği kişiye özel bir protokoldür.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                1. Sıvı Yüz Germe Nasıl Yapılır?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                İşlemde, farklı derinliklerde mikro enjeksiyonlar kullanılarak cilt altına özel içerikli ürünler uygulanır. Bu içerikler:
              </p>
              
              <div className="bg-blue-50 rounded-xl p-6 mb-6">
                <ul className="list-disc list-inside text-blue-800 space-y-2">
                  <li><strong>Hyaluronik asit</strong> (nem ve dolgunluk sağlar)</li>
                  <li><strong>Aminoasit ve vitamin kompleksleri</strong> (cilt yenilenmesini destekler)</li>
                  <li><strong>Kollajen ve elastin destekleri</strong> (sıkılaştırıcı etki oluşturur)</li>
                </ul>
                <p className="text-blue-800 mt-3">
                  şeklinde kişiye özel karışımlar halindedir.
                </p>
                <p className="text-blue-800 mt-2">
                  Uygulama ortalama 30–40 dakika sürer ve anestezik kremle ağrısız şekilde yapılır.
                </p>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                2. Kullanılan Başlıca İçerikler
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        İçerik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Görevi
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Etkisi
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Hyaluronik Asit</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cildi nemlendirir, dolgunluk sağlar</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hemen gözle görülür tazelik etkisi</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kollajen Peptitleri</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt dokusunu sıkılaştırır</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Elastikiyeti artırır, lifting etkisi yaratır</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Vitamin C & E</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Antioksidan etki, serbest radikalleri nötralize eder</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt parlaklığını artırır</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Aminoasitler</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hücre yenilenmesini destekler</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cildin canlı ve genç görünümünü korur</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Koenzim Q10 & DMAE</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Kas tonusunu güçlendirir</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Yüz hatlarında belirgin sıkılaşma sağlar</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6">
                Bu içerikler genellikle tek seansta kombine edilir ve cilt yapısına göre kişiye özel hazırlanır.
              </p>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                3. Etki Süresi ve Kalıcılık
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Sıvı yüz germe etkisi genellikle hemen görülür ve 6–12 ay arasında kalıcılığını sürdürür. Düzenli seanslarla (yılda 1–2 kez) etki süresi uzar.
              </p>

              <div className="bg-green-50 rounded-xl p-6 mb-6">
                <h3 className="text-lg font-semibold text-green-900 mb-3">Etkinin uzunluğu şu faktörlere bağlıdır:</h3>
                <ul className="list-disc list-inside text-green-800 space-y-2">
                  <li>Kullanılan ürünün kalitesi</li>
                  <li>Cilt tipi ve yaş</li>
                  <li>Yaşam tarzı (sigara, güneş, stres)</li>
                  <li>Uygulamanın derinliği ve tekniği</li>
                </ul>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                4. Sıvı Yüz Germe Kimler İçin Uygundur?
              </h2>
              <p className="text-gray-700 mb-4 leading-relaxed">
                Bu işlem, cerrahi yüz germe istemeyen ve cildinde hafif sarkma, elastikiyet kaybı veya solgunluk olan kişiler için idealdir.
              </p>

              <div className="grid md:grid-cols-2 gap-6 mb-8">
                <div className="bg-blue-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-blue-900 mb-3">Uygun adaylar:</h3>
                  <ul className="list-disc list-inside text-blue-800 space-y-2">
                    <li>30–60 yaş arası kişiler</li>
                    <li>Cildinde elastikiyet kaybı başlayanlar</li>
                    <li>Doğal görünümlü, ameliyatsız gençleşme isteyenler</li>
                  </ul>
                </div>

                <div className="bg-red-50 rounded-xl p-6">
                  <h3 className="text-lg font-semibold text-red-900 mb-3">Uygulanmaz:</h3>
                  <ul className="list-disc list-inside text-red-800 space-y-2">
                    <li>Hamilelik, emzirme</li>
                    <li>Aktif enfeksiyon</li>
                    <li>Kan sulandırıcı ilaç kullanımı</li>
                  </ul>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                5. Sıvı Yüz Germe ile Dolgu Arasındaki Fark
              </h2>
              
              {/* Table */}
              <div className="overflow-x-auto mb-8">
                <table className="w-full bg-white border border-gray-300 rounded-lg">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Özellik
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Sıvı Yüz Germe
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border-b">
                        Dolgu
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Amaç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Cilt kalitesini artırmak ve sıkılaştırmak</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacim kazandırmak</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">İçerik</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hyaluronik asit + vitamin + kolajen</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Genellikle saf hyaluronik asit</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Etkisi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Doğal lifting ve nem artışı</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Bölgesel dolgunluk</td>
                    </tr>
                    <tr className="bg-gray-50">
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Kalıcılık Süresi</td>
                      <td className="px-6 py-4 text-sm text-gray-900">6–12 ay</td>
                      <td className="px-6 py-4 text-sm text-gray-900">12–18 ay</td>
                    </tr>
                    <tr>
                      <td className="px-6 py-4 text-sm text-gray-900 font-medium">Sonuç</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Doğal, ışıltılı cilt</td>
                      <td className="px-6 py-4 text-sm text-gray-900">Hacimli, şekillendirilmiş yüz hatları</td>
                    </tr>
                  </tbody>
                </table>
              </div>

              <p className="text-gray-700 mb-6 font-medium">
                Her iki yöntem bir arada kullanıldığında etkisi daha belirgin hale gelir.
              </p>

              {/* Image placeholder for article */}
              <div className="relative h-64 rounded-xl overflow-hidden mb-8">
                <Image
                  src="/images/terleme-tedavisi.webp"
                  alt="Sıvı yüz germe işlemi ve içerikler"
                  fill
                  className="object-cover"
                />
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Sık Sorulan Sorular (FAQ)
              </h2>

              <div className="space-y-6 mb-8">
                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">1. Sıvı yüz germe işlemi ağrılı mı?</h3>
                  <p className="text-gray-700">Hayır. İşlem öncesi uygulanan anestezik krem sayesinde rahatsızlık hissedilmez.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">2. Etkisi hemen görülür mü?</h3>
                  <p className="text-gray-700">Evet, ilk seanstan sonra ciltte parlaklık ve gerginlik hemen fark edilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">3. Kaç seans gerekir?</h3>
                  <p className="text-gray-700">Cilt durumuna göre 1–3 seans yeterli olur; yılda 1 idame seansı önerilir.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">4. Sonrasında morarma veya şişlik olur mu?</h3>
                  <p className="text-gray-700">Nadiren hafif kızarıklık veya ödem olabilir; birkaç gün içinde geçer.</p>
                </div>

                <div className="bg-gray-50 rounded-lg p-6">
                  <h3 className="font-semibold text-gray-900 mb-2">5. Sıvı yüz germe ile botoks aynı anda yapılabilir mi?</h3>
                  <p className="text-gray-700">Evet, kombine tedaviyle hem lifting hem kırışıklık azaltma etkisi sağlanabilir.</p>
                </div>
              </div>

              <h2 className="text-2xl font-bold text-gray-900 mb-4 mt-8">
                Kaynakça
              </h2>
              <ul className="list-disc list-inside text-gray-700 space-y-2 mb-8">
                <li>European Journal of Aesthetic Medicine (2023) – "Liquid Face Lift Techniques and Efficacy"</li>
                <li>Türk Dermatoloji Derneği – Sıvı Yüz Germe Protokolü</li>
                <li>Mayo Clinic – "Non-Surgical Facial Rejuvenation"</li>
                <li>Harvard Health Publishing – "Liquid Facelift Explained"</li>
                <li>ISAPS 2024 Global Report – Minimal Invasive Aesthetic Trends</li>
              </ul>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 mb-8">
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#sıvıyüzgerme</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#hyaluronikasit</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#kolajen</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#ameliyatsızestetik</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#lifting</span>
                <span className="bg-indigo-100 text-indigo-800 px-3 py-1 rounded-full text-sm">#ciltgençleştirme</span>
              </div>
            </div>

            {/* Call to Action */}
            <div className="bg-gradient-to-r from-blue-600 to-purple-600 rounded-2xl p-8 text-white text-center mt-12">
              <h3 className="text-2xl font-bold mb-4">Sıvı Yüz Germe Uygulaması İçin Randevu Alın</h3>
              <p className="mb-6">Ameliyatsız yüz gençleştirme ve lifting etkisi için uzman doktorumuzdan detaylı bilgi alın.</p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Link
                  href={`https://wa.me/905438999586?text=${encodeURIComponent('Sıvı yüz germe uygulaması hakkında bilgi almak istiyorum.')}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center justify-center px-6 py-3 bg-green-500 text-white rounded-full hover:bg-green-600 transition-colors"
                >
                  <FaWhatsapp className="w-5 h-5 mr-2" />
                  WhatsApp ile İletişim
                </Link>
                <Link
                  href="/tr/iletisim"
                  className="inline-flex items-center justify-center px-6 py-3 bg-white text-blue-600 rounded-full hover:bg-gray-100 transition-colors"
                >
                  Randevu Al
                </Link>
              </div>
            </div>
          </div>
        </article>

        {/* Related Articles */}
        <div className="mt-12">
          <h3 className="text-2xl font-bold text-gray-900 mb-6">İlgili Yazılar</h3>
          <div className="grid md:grid-cols-2 gap-6">
            <Link
              href="/tr/blog/dolgu-islemleri-yuz-liftingi-saglar-mi"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Dolgu İşlemleri Yüz Liftingi Sağlar mı?</h4>
              <p className="text-gray-600 text-sm">Dolgu uygulamalarının yüz liftingi etkisi hakkında bilgiler.</p>
            </Link>
            <Link
              href="/tr/blog/mezoterapi-etkileri-ne-zaman-gorulur"
              className="bg-white rounded-2xl shadow-lg p-6 hover:shadow-xl transition-all duration-300 hover:-translate-y-1"
            >
              <h4 className="text-lg font-bold text-gray-900 mb-2">Mezoterapi Etkileri Ne Zaman Görülür?</h4>
              <p className="text-gray-600 text-sm">Mezoterapi sonuçlarının görünme süresi ve kalıcılık.</p>
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}

